import type {
  GenerationTypesResult,
  ModelsListResult,
  ParametersResult,
} from '@/features/docs/services/generation-catalog-docs.service'
import {
  buildGenerationTypes,
  buildModelsList,
  buildParameters,
} from '@/features/docs/services/generation-catalog-docs.service'

function formatPrice(millidollars: number): string {
  return `$${(millidollars / 1000).toFixed(2)}`
}

export function modelsListToMarkdown(data: ModelsListResult = buildModelsList()): string {
  const lines: Array<string> = [
    `${data.total} models across ${data.families.length} providers: ${data.families.join(', ')}.`,
    '',
    '| Model | Family | Generation types | Starting price |',
    '| --- | --- | --- | --- |',
  ]

  for (const model of data.models) {
    lines.push(
      `| \`${model.id}\` (${model.displayName}) | ${model.family} | ${model.variantLabels.join(', ')} | ${formatPrice(model.minPrice)} per ${model.priceUnit.replace('per-', '')} |`,
    )
  }

  lines.push(
    '',
    '> Prices shown are the minimum for that model/type — actual cost varies with settings like resolution, duration, and quality. See the Parameters page for tunable fields.',
  )

  return lines.join('\n')
}

export function generationTypesToMarkdown(
  data: GenerationTypesResult = buildGenerationTypes(),
): string {
  const lines: Array<string> = [`${data.totalTypes} generation types currently backed by at least one model.`, '']

  for (const { category, types } of data.categories) {
    lines.push(`## ${category}`, '')
    for (const t of types) {
      lines.push(
        `### ${t.label} (\`${t.type}\`)`,
        '',
        `- Models: ${t.models.map((m) => `\`${m}\``).join(', ')}`,
        `- Providers: ${t.families.join(', ')}`,
        `- Parameters: ${t.params.length > 0 ? t.params.map((p) => `\`${p}\``).join(', ') : '_none_'}`,
        '',
      )
    }
  }

  return lines.join('\n')
}

export function parametersToMarkdown(data: ParametersResult = buildParameters()): string {
  const byCategory = new Map<string, typeof data.params>()
  for (const param of data.params) {
    if (!byCategory.has(param.category)) byCategory.set(param.category, [])
    byCategory.get(param.category)!.push(param)
  }

  const lines: Array<string> = [
    `${data.total} canonical parameters accepted in a generation request's \`modelSettings\` object.`,
    '',
  ]

  for (const [category, params] of byCategory) {
    lines.push(`## ${category}`, '')
    for (const p of params) {
      const bits = [`\`${p.name}\` (${p.type})`]
      if (p.enumValues) bits.push(`allowed: ${p.enumValues.map((v) => `\`${v}\``).join(', ')}`)
      if (p.default) bits.push(`default: \`${p.default}\``)
      lines.push(`- ${bits.join(' — ')} — ${p.description}`)
      if (p.usedByModels.length > 0) {
        lines.push(`  - Used by: ${p.usedByModels.map((m) => `\`${m}\``).join(', ')}`)
      }
    }
    lines.push('')
  }

  return lines.join('\n')
}

// The "Endpoints" page has no separate live data source (it's a hand-written
// walkthrough of the /v1/generations HTTP surface), so this is authored
// directly — kept in sync with the real request/response shapes and error
// behavior verified against app/src/routes/api/generations/*.ts and
// app/src/server/application/generation/dto/GenerationDto.ts.
export function generationsEndpointsMarkdown(): string {
  return `The Generations API creates and manages async image/video generation jobs.

## Base URL

\`\`\`
https://api.modelstack.cc
\`\`\`

## Authentication

\`\`\`
Authorization: Bearer sk_YOUR_API_KEY
\`\`\`

## POST /v1/generations

Create a new generation. Returns immediately with a pending job.

Request body:

| Field | Type | Required | Description |
| --- | --- | --- | --- |
| \`type\` | string | Yes | Generation type, e.g. \`TEXT_TO_IMAGE\`, \`TEXT_TO_VIDEO\`, \`IMAGE_TO_IMAGE\` — see the Generation Types page for the full list |
| \`prompt\` | string | Yes | Text prompt, max 5000 characters |
| \`model\` | string | Yes | Model identifier, e.g. \`google/nano-banana-2-lite\` — see the Model Catalog |
| \`modelSettings\` | object | No | Model-specific parameters (aspect ratio, resolution, duration, etc.) — see the Parameters page |
| \`inputMedia\` | array | No | \`[{ type: "image"\\|"video"\\|"audio", key: string }]\` — keys from \`POST /v1/media/upload\` |

\`\`\`bash
curl -X POST "https://api.modelstack.cc/v1/generations" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "TEXT_TO_IMAGE",
    "prompt": "A serene mountain landscape at sunset",
    "model": "google/nano-banana-2-lite",
    "modelSettings": { "aspect_ratio": "16:9" }
  }'
\`\`\`

Response:

\`\`\`json
{ "data": { "id": "<generation-id>" } }
\`\`\`

Only the id is returned — poll \`GET /v1/generations/:id\` for status and output.

## GET /v1/generations/:id

\`\`\`bash
curl "https://api.modelstack.cc/v1/generations/<generation-id>" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY"
\`\`\`

Response:

\`\`\`json
{
  "data": {
    "id": "<generation-id>",
    "type": "TEXT_TO_IMAGE",
    "prompt": "...",
    "model": "google/nano-banana-2-lite",
    "status": "completed",
    "outputMediaUrls": ["media/<user-id>/<generation-id>/0.png"],
    "creditsUsed": 40,
    "creditsRefunded": false,
    "createdAt": "2026-01-01T00:00:00.000Z",
    "completedAt": "2026-01-01T00:00:12.000Z"
  }
}
\`\`\`

\`status\` is one of \`pending\`, \`processing\`, \`completed\`, \`failed\`, \`deleted\`.

**\`outputMediaUrls\` holds storage keys, not downloadable URLs.** Exchange each key for a temporary signed URL:

\`\`\`bash
curl "https://api.modelstack.cc/v1/media/presigned-url?key=media/<user-id>/<generation-id>/0.png" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY"
\`\`\`

The signed URL expires after 1 hour.

## GET /v1/generations

List generations for the authenticated user, newest first.

| Query param | Type | Default | Description |
| --- | --- | --- | --- |
| \`limit\` | number | 25 | Max records (max 100) |
| \`offset\` | number | 0 | Pagination offset |
| \`status\` | string | — | Filter by status |

\`\`\`bash
curl "https://api.modelstack.cc/v1/generations?limit=10&status=completed" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY"
\`\`\`

Response:

\`\`\`json
{ "data": [ /* generation objects, same shape as GET /v1/generations/:id */ ], "total": 42, "limit": 10, "offset": 0 }
\`\`\`

## DELETE /v1/generations/:id

Delete a **completed or failed** generation (deleting a pending/processing job is rejected).

\`\`\`bash
curl -X DELETE "https://api.modelstack.cc/v1/generations/<generation-id>" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY"
\`\`\`

Response:

\`\`\`json
{ "success": true }
\`\`\`

## Errors

Error responses are **not** uniformly shaped across this API — the shape depends on which layer rejects the request:

- Requests rejected before they reach the app (auth failure, insufficient prepaid balance) return a nested shape:
  \`\`\`json
  { "error": { "message": "...", "type": "invalid_api_key" } }
  \`\`\`
  (401, \`type: "invalid_api_key"\`), or
  \`\`\`json
  { "error": { "message": "Insufficient balance", "type": "insufficient_balance", "code": "insufficient_balance" }, "balance": { /* ... */ } }
  \`\`\`
  (402). This pre-check uses a rough flat cost estimate, not the exact per-request price — it can pass and still have the real balance check fail later (see below).
- Requests that reach the app but fail validation/lookup return a **flat string**, not a nested object:
  \`\`\`json
  { "error": "type, prompt, and model are required" }
  \`\`\`
  (400), or \`{ "error": "Unauthorized" }\` (401), \`{ "error": "<not-found message>" }\` (404).
- Unexpected failures — including the real (non-estimated) balance check failing, and a couple of known edge cases below — return \`{ "error": "Internal server error" }\` (500) rather than a specific 4xx.

**Known edge-case behavior, not (yet) clean error responses:**
- \`GET /v1/generations/:id\` for a nonexistent id returns HTTP 200 with \`{ "data": null }\`, not a 404.
- \`DELETE /v1/generations/:id\` on a pending/processing generation returns HTTP 500, not a 400.
- There is currently no enforced rate limit on \`/v1/generations*\` — don't rely on a specific requests-per-minute figure.
`
}
